/**
 * postMessage bridge protocol between an ajobthing host page and the chat-emp
 * iframe. Defined once here; imported by the client bridge and inlined by the
 * widget loader (widget.js keeps zero runtime deps, so it copies these shapes).
 *
 * Envelope: { source: 'ajt-chat', version, type, payload }. Both sides validate
 * event.origin against an exact-match allowlist and always post with an explicit
 * targetOrigin — never '*'.
 */

export const BRIDGE_SOURCE = 'ajt-chat' as const;
export const BRIDGE_VERSION = 1 as const;

/** Host → iframe. */
export type InboundType =
  | 'init'
  | 'set-context'
  | 'set-theme'
  | 'set-viewport'
  | 'set-gamification'
  | 'open-chat'
  | 'send-message'
  | 'send-job'
  // stream-chat parity: the host reports that an `event`-mode CTA it was asked
  // to resolve has failed, so the row can stop spinning.
  | 'message-cta-click-error'
  // The pane's backdrop was clicked: close it.
  | 'close-pane'
  // The host could not mint the handoff a `session-expired` asked for.
  | 'session-lost';

/** Iframe → host. Names + payloads mirror the stream-chat web component's public
 *  host events so a host can consume them with the same handler it wrote there
 *  (see docs). The host's bridge switch ignores types it doesn't handle, so new
 *  events never break it — it opts in per event. Do NOT rename `ready`,
 *  `session-expired`, or `unread-count`: the host keys on those exact names. */
export type OutboundType =
  | 'ready'
  | 'session-expired'
  | 'unread-count'
  | 'callout'
  | 'position'
  | 'conversation-opened'
  | 'conversation-started'
  | 'send-result'
  | 'error'
  | 'open'
  | 'close'
  | 'start-chat-click'
  | 'expand'
  | 'mode'
  | 'view-profile'
  // stream-chat parity — core interaction events:
  | 'channel-select'
  | 'channel-search'
  | 'back-to-channel-list'
  | 'message-sent'
  | 'active-channel'
  | 'channel-header-avatar-click'
  | 'quick-reply-click'
  | 'channel-list-loaded'
  | 'sidebar-application-action'
  | 'sidebar-application-action-error'
  | 'template-click'
  | 'template-use'
  | 'template-create'
  | 'template-save'
  | 'callout-reply'
  | 'callout-later'
  // in-message CTA buttons. `-click` fires on every click in every mode: it is
  // the trigger for `event` mode and the analytics signal for the other two.
  | 'message-cta-click'
  | 'message-cta-error'
  // end-chat funnel — the wired subset (the rest of the funnel is still deferred):
  | 'end-chat-decline-sent'
  | 'end-chat-pill-click'
  // gamification — name and payload mirror the web component's `gamification-action`
  // so a host reuses the handler it already wrote there.
  | 'gamification-action';

export type BridgeType = InboundType | OutboundType;

export interface ChatContext {
  jobId?: string;
  candidateId?: string;
}

/** The host's coin budget. Mirrors the web component's `gamification-config`. */
export interface GamificationCoins {
  init_chat: number;
  first_reply: number;
  /** Remaining global budget. 0 hides the nudge everywhere. */
  limit: number;
  claimed: number;
}

export interface GamificationConfig {
  coins: GamificationCoins;
}

/** Payload of `gamification-action`. Field names match the web component's detail. */
export interface GamificationActionDetail {
  channelId: string;
  companyId: string;
  jobseekerId?: string;
  jobId?: string;
  /** `init_chat` is reported when a compose becomes a real conversation. The coin
   *  has always been in `GamificationCoins`; nothing emitted it until start-chat
   *  moved into the widget. */
  type: 'first_reply' | 'init_chat';
  coins: number;
}

/**
 * Made of what the HOST already has on screen, because the chat system has no
 * channel or id yet. The EMPLOYER's ids are never taken from here: they come
 * from the verified session, so a tampered payload cannot open as another
 * company.
 */
export interface StartChatTarget {
  applicant: { id: string; name: string; image?: string; locked?: boolean };
  company: { name: string; image?: string };
  /**
   * Opaque, and deliberately NOT a union. Six values reach these entry points
   * today — `job_application`, `direct_message`, `candidate_search`,
   * `candidate_search_unlocked`, `candidate_search_my_folder`, `private_db` —
   * and others exist in apps we don't own (`career_fair`, `interview`,
   * `marketing_campaign`). A union here would reject the next one someone adds.
   *
   * Only two behaviours may branch on it, and both are upstream rules rather
   * than ours: the job-bound channel suffix and the ownership check.
   */
  source: string;
  /** Opaque. Becomes `source.meta` upstream verbatim; the shape differs entirely
   *  per source, so nothing here interprets it. */
  sourceMeta?: Record<string, unknown>;
  /** Present only for a job-bound source; drives the `-job-<id>` channel suffix. */
  job?: { id: string };
}

/**
 * Open onto a channel the host knows (View / Reply) or onto a target with no
 * channel yet (Start: compose, create on send).
 *
 * `requestId` is not decoration: the host may ask twice with an identical
 * payload, and without it the widget would ignore the second and stay on the
 * wrong person.
 */
export type OpenChatPayload = { requestId: number } & (
  { channelId: string; draft?: string } | (StartChatTarget & { draft?: string })
);

/** Headless: send without ever showing the panel. */
export type SendMessagePayload = { requestId: number } & StartChatTarget & { text: string };

/** Headless, into a chat that already exists: one of the company's postings as a
 *  card, with the employer's words above it. Answered with `send-result`. */
export type SendJobPayload = { requestId: number; channelId: string; jobId: string; text?: string };

/** A compose became a real conversation. The host uses this to flip its CTA from
 *  "Start chat" to "View chat" without a refetch, and to award the init credit. */
export interface ConversationStartedPayload {
  requestId: number;
  channelId: string;
  applicantId: string;
  jobId?: string;
}

/** The answer to one `send-message`. Correlated by `requestId` because a host may
 *  have several in flight. `code` is the AppError code, so the host picks its own
 *  copy rather than rendering ours. */
export interface SendResultPayload {
  requestId: number;
  ok: boolean;
  channelId?: string;
  code?: string;
}

/** Payload of `message-cta-click`. Field names match the web component's detail. */
export interface CtaClickDetail {
  messageId: string;
  channelId: string;
  actionId: string;
  payload?: Record<string, unknown>;
}

/** Payload of `message-cta-error` (outbound) and `message-cta-click-error`
 *  (inbound). Same shape both ways, as in the web component. */
export interface CtaErrorDetail {
  messageId: string;
  actionId: string;
  reason?: string;
}

export interface InboundPayloads {
  init: { handoffToken: string; context?: ChatContext; theme?: string };
  'set-context': ChatContext;
  'set-theme': { theme: string };
  /**
   * The HOST page's viewport class. The widget renders inside a ~424px iframe,
   * so its own media queries always read "narrow" and it would sit in the mobile
   * layout on every desktop. Only the host knows the real viewport, so it says
   * so — see `widget.ts`, which already measures it to size the frame.
   */
  'set-viewport': { mobile: boolean };
  /**
   * The host's coin budget, re-sent whenever it changes (an award decrements
   * `limit`). Absent until the host sends it, and chat-emp shows no nudge and
   * reports nothing until then — it never invents an amount.
   */
  'set-gamification': GamificationConfig;
  /** Open on a channel the host knows, or compose against a target that has none. */
  'open-chat': OpenChatPayload;
  /** Send with no UI at all — the host's auto-send path. Answered with
   *  `send-result`, never rendered. */
  'send-message': SendMessagePayload;
  /** A posting into an existing chat, as a card. Answered with `send-result`. */
  'send-job': SendJobPayload;
  /**
   * An `event`-mode CTA the host took on has failed on its side. Clears the
   * pending state for that message so the row re-enables.
   *
   * There is no matching success message on purpose: a host that resolved the
   * action tells the BACKEND, which seals the row and broadcasts
   * `message.updated`. Letting a host grey a row directly would put the
   * terminal state in the one place that cannot be trusted with it.
   */
  'message-cta-click-error': CtaErrorDetail;
  'close-pane': Record<string, never>;
  'session-lost': Record<string, never>;
}

/** How the launcher was opened — stream-chat parity, minus `programmatic`:
 *  this embed exposes no imperative open. */
export type OpenMethod = 'fab' | 'callout';

/** How the panel was closed. `close-pill` is the launcher's own X, matching
 *  stream-chat; `panel-header` is the × in the panel header. */
export type CloseMethod = 'esc' | 'close-pill' | 'panel-header';

/**
 * What a host asks the full page to do with the chat it hands over.
 *
 * The widget sends `details` and `end` because it has no room for the reference
 * column and ending starts a deletion clock. A host page sends `resume`.
 *
 * Not `reject`: what ending means is the conversation's own business, and a
 * sender that spelled the variant out could disagree with the page.
 *
 * Travels as `?open=`. `widget.js` mirrors this list by hand. The list is the
 * declaration and the type is read off it: a separately written union would let
 * a fourth intent typecheck while `toHandoffIntent` went on rejecting it.
 */
const HANDOFF_INTENTS = ['details', 'end', 'resume'] as const;

export type HandoffIntent = (typeof HANDOFF_INTENTS)[number];

/** Read `?open=` off the URL, ignoring anything that isn't one of ours. */
export function toHandoffIntent(raw: string | null): HandoffIntent | null {
  return HANDOFF_INTENTS.find((intent) => intent === raw) ?? null;
}

/**
 * A separate axis from `HandoffIntent`: an intent acts on a named conversation,
 * a tab names a place with no conversation in it. The word is the one on screen
 * (`info`), not the rail's internal id (`broadcast`).
 *
 * A plain union rather than a derived type, since nothing parses this and
 * `ChatView.sectionFromTab` is already the safe reader. The cost:
 * `widget.js`'s hand-written allowlist has nothing to check itself against, so a
 * second tab added here is dropped silently by the loader.
 */
export type HandoffTab = 'info';

/**
 * What the widget hands the full page when it leaves for it.
 *
 * Every field optional: a chat (`?channel=`), an act to carry out on it
 * (`?open=`), or a section to land on (`?tab=`). The bare `{}` is the plain
 * expand — take me to the page, nothing waiting there. `tab` arrives alone;
 * see `HandoffTab` for why a section brings no conversation with it.
 */
export interface ExpandPayload {
  channelId?: string;
  open?: HandoffIntent;
  tab?: HandoffTab;
}

export interface OutboundPayloads {
  ready: Record<string, never>;
  'session-expired': Record<string, never>;
  /** Offset from the viewport's bottom-right corner; `dragging` asks the host to
   *  stretch our frame so the widget can move inside it (widget.js does this). */
  position: { right: number; bottom: number; dragging: boolean };
  /** Unread CHATS (`UNREAD_CHATS_QUERY`) for the host's own badge — the number
   *  the launcher draws beside its dot, on both surfaces. Print it as-is. */
  'unread-count': { n: number };
  /** The "N chats need your reply" nudge appeared or went. The iframe embed
   *  needs it to resize the launcher frame — see `calloutStyle` in widget.js —
   *  since a collapsed frame is only as big as the launcher pill. `width`/
   *  `height` are the measured launcher+nudge stack, sent rather than assumed so
   *  the frame cannot be wrong about copy that wraps or tokens that change;
   *  absent when the nudge is going away, and the loader falls back to its own
   *  numbers if a host is on an older client. */
  callout: { visible: boolean; width?: number; height?: number };
  'conversation-opened': { channelId: string };
  /** A compose became a real conversation — distinct from `conversation-opened`,
   *  which fires for one that already existed. */
  'conversation-started': ConversationStartedPayload;
  /** The outcome of one headless `send-message`, correlated by `requestId`. */
  'send-result': SendResultPayload;
  error: { code: string };
  /**
   * Mirrors the stream-chat web component's `widget-open`/`widget-close`
   * `method` (FAB vs. nudge) so a host reuses its existing funnel. Optional because the widget
   * also reports its initial closed state, which nobody caused. `mobile-header`
   * is spelled `panel-header`: our header close is not mobile-only.
   */
  open: { method?: OpenMethod };
  close: { method?: CloseMethod };
  /** The user asked to start a new chat; the HOST owns the picker popup.
   *  Deliberately empty — the host minted the handoff that established this
   *  session, so it already knows who is asking. */
  'start-chat-click': Record<string, never>;
  /**
   * The user asked to leave the widget for the full chat page. The HOST owns
   * this: the widget lives in a ~424px iframe, so routing there itself would
   * only load `/chat` inside that frame — the full workspace crammed into the
   * launcher. Where the chat page lives, and how one gets to it, is the host's
   * call (`widget.js` handles it by default; see its `chatPageUrl` option).
   *
   * What it carries is `ExpandPayload`'s to say.
   */
  expand: ExpandPayload;
  /** Whether a `mode: 'pane'` embed actually runs as one: false only when the
   *  session failed. Sent once the session resolves. */
  mode: { pane: boolean };
  /** Pane mode: the employer pressed the header identity. `jobseekerId` is bare (no `js_`). */
  /** `channelId` is absent when the pane opened on an applicant with no chat yet. */
  'view-profile': { channelId?: string; jobseekerId: string };
  // stream-chat parity. `channelType`/`id` are split from the Stream cid
  // (`type:id`) to match stream-chat's shape; `channelId` carries the id part.
  'channel-select': { id: string; type: string; name: string; memberCount: number; unread: number };
  'channel-search': { query: string; resultsCount: number };
  'back-to-channel-list': { channelId: string; channelType: string; channelName: string };
  'message-sent': {
    messageId: string;
    text: string;
    channelId: string;
    channelType: string;
    timestamp: string;
  };
  // stream-chat's active-channel / avatar-click also carry the raw Stream
  // channel `data` + `members` map; chat-emp's domain model doesn't keep those,
  // so we send the channel identity (id/type) it does have.
  'active-channel': { id: string; type: string };
  'channel-header-avatar-click': { id: string; type: string };
  'quick-reply-click': { message: string; source?: string; auditId?: string };
  'channel-list-loaded': {
    channels: { id: string; type: string; name: string; memberCount: number }[];
    count: number;
  };
  'sidebar-application-action': { channelId: string; channelType: string; status: string };
  'sidebar-application-action-error': { channelId: string; channelType: string; status: string };
  'template-click': Record<string, never>;
  'template-use': { id: number; name: string; template: string };
  'template-create': { name: string; template: string };
  'template-save': { id: number; name: string; template: string };
  'callout-reply': { count: number };
  'callout-later': { count: number };
  // Every CTA click, in every mode. `url` and `reply` also act on their own;
  // `event` does nothing but this, and waits for the host to resolve it.
  'message-cta-click': CtaClickDetail;
  // A `url`-mode request failed, or a `reply`-mode send did. Not emitted for
  // `event` mode: the host is the one that would know, and it reports back
  // inbound instead.
  'message-cta-error': CtaErrorDetail;
  // end-chat funnel (the wired subset). `alsoRejected` marks the End & reject path;
  // `variant` is the closing kind (`close`/`reject`) the dialog opened for.
  'end-chat-decline-sent': { channelId: string; channelType: string; alsoRejected: boolean };
  'end-chat-pill-click': { channelId: string; channelType: string; variant: string };
  'gamification-action': GamificationActionDetail;
}

export interface BridgeEnvelope<T extends BridgeType = BridgeType> {
  source: typeof BRIDGE_SOURCE;
  version: number;
  type: T;
  payload?: unknown;
}

export function makeMessage<T extends BridgeType>(type: T, payload?: unknown): BridgeEnvelope<T> {
  return { source: BRIDGE_SOURCE, version: BRIDGE_VERSION, type, payload };
}

/**
 * Narrow an unknown postMessage `data` to a bridge envelope of the current
 * version. Returns null for anything foreign, malformed, or from a future
 * major version (forward-compat: unknown versions are ignored, not thrown).
 */
export function parseEnvelope(data: unknown): BridgeEnvelope | null {
  if (typeof data !== 'object' || data === null) return null;
  const d = data as Record<string, unknown>;
  if (d.source !== BRIDGE_SOURCE) return null;
  if (d.version !== BRIDGE_VERSION) return null;
  if (typeof d.type !== 'string') return null;
  return {
    source: BRIDGE_SOURCE,
    version: BRIDGE_VERSION,
    type: d.type as BridgeType,
    payload: d.payload,
  };
}

/** True when `origin` is one of the exact-match allowed origins. */
export function isAllowedOrigin(origin: string, allowlist: readonly string[]): boolean {
  return allowlist.includes(origin);
}
